// Privacy Policy content for SureDox
// Last updated: January 2025

export const privacyPolicyContent = `
<h1>SureDox Privacy Policy</h1>

<p class="effective-date"><strong>Effective Date:</strong> 27 January 2025<br />
<strong>Last Updated:</strong> 27 January 2025</p>

<hr />

<h2>1. Introduction</h2>

<p>Boone and Boo (Pty) Ltd, trading as SureDox ("SureDox", "we", "us", or "our"), is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and protect personal information in accordance with the Protection of Personal Information Act 4 of 2013 ("POPIA").</p>

<p>This policy applies to personal information we collect about you as a user of our services. For information about how we process documents you upload for redaction (where we act as an Operator on your behalf), please see our <a href="/terms">Terms of Service</a>.</p>

<hr />

<h2>2. Who We Are</h2>

<p><strong>Boone and Boo (Pty) Ltd</strong> (trading as SureDox)<br />
Registration Number: 2025 / 489700 / 07<br />
Physical Address: 9 Clarence Avenue, Craighall Park, Johannesburg</p>

<p><strong>Information Officer:</strong> Eben Smit<br />
<strong>Email:</strong> <a href="mailto:eben@suredox.co.za">eben@suredox.co.za</a></p>

<hr />

<h2>3. Personal Information We Collect</h2>

<p>We collect the following categories of personal information:</p>

<h3>3.1 Account Information</h3>
<ul>
<li>Name and surname</li>
<li>Email address</li>
<li>Password (encrypted)</li>
<li>Profile information you provide</li>
</ul>

<h3>3.2 Payment Information</h3>
<ul>
<li>Billing address</li>
<li>Payment card details (processed securely by Paystack - we do not store full card numbers)</li>
<li>Transaction history</li>
</ul>

<h3>3.3 Usage Information</h3>
<ul>
<li>Service usage patterns</li>
<li>Feature interactions</li>
<li>Credit usage and balances</li>
<li>Document processing history (metadata only, not content)</li>
</ul>

<h3>3.4 Technical Information</h3>
<ul>
<li>IP address</li>
<li>Browser type and version</li>
<li>Device information</li>
<li>Operating system</li>
<li>Access times and dates</li>
</ul>

<hr />

<h2>4. How We Collect Personal Information</h2>

<p>We collect personal information:</p>

<ul>
<li><strong>Directly from you:</strong> When you create an account, purchase credits, or contact us</li>
<li><strong>Automatically:</strong> Through cookies and similar technologies when you use our website</li>
<li><strong>From third parties:</strong> Authentication providers (Google) if you choose to sign in with a third-party account</li>
</ul>

<hr />

<h2>5. Purpose of Processing</h2>

<p>We process your personal information for the following purposes:</p>

<table>
<thead>
<tr><th>Purpose</th><th>Legal Basis (POPIA)</th></tr>
</thead>
<tbody>
<tr><td>Providing our services</td><td>Contract performance</td></tr>
<tr><td>Creating and managing your account</td><td>Contract performance</td></tr>
<tr><td>Processing payments</td><td>Contract performance</td></tr>
<tr><td>Communicating about your account</td><td>Contract performance / Legitimate interest</td></tr>
<tr><td>Improving our services</td><td>Legitimate interest</td></tr>
<tr><td>Ensuring security and preventing fraud</td><td>Legitimate interest / Legal obligation</td></tr>
<tr><td>Compliance with legal obligations</td><td>Legal obligation</td></tr>
<tr><td>Marketing (with your consent)</td><td>Consent</td></tr>
</tbody>
</table>

<hr />

<h2>6. Disclosure of Personal Information</h2>

<p>We may share your personal information with:</p>

<h3>6.1 Service Providers</h3>
<p>Third parties who help us deliver our services:</p>

<table>
<thead>
<tr><th>Provider</th><th>Purpose</th><th>Location</th></tr>
</thead>
<tbody>
<tr><td>Google Cloud Platform / Firebase</td><td>Hosting, storage, authentication</td><td>EU/US</td></tr>
<tr><td>Anthropic (Claude API)</td><td>AI-powered PII detection</td><td>United States</td></tr>
<tr><td>DocuPipe</td><td>Image-based PDF processing</td><td>United States (New York)</td></tr>
<tr><td>Paystack</td><td>Payment processing</td><td>South Africa</td></tr>
</tbody>
</table>

<h3>6.2 Legal Requirements</h3>
<p>We may disclose information if required by law, court order, or to protect our legal rights.</p>

<h3>6.3 Business Transfers</h3>
<p>In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.</p>

<hr />

<h2>7. Cross-Border Transfers</h2>

<p>Your personal information may be transferred to and processed in countries outside South Africa, including the United States. We ensure such transfers comply with Section 72 of POPIA by:</p>

<ul>
<li>Using service providers bound by appropriate data protection agreements</li>
<li>Ensuring adequate safeguards are in place</li>
<li>Obtaining your consent where required</li>
</ul>

<hr />

<h2>8. Data Retention</h2>

<p>We retain your personal information for the following periods:</p>

<table>
<thead>
<tr><th>Category</th><th>Retention Period</th></tr>
</thead>
<tbody>
<tr><td>Account information</td><td>Duration of account + 30 days after deletion</td></tr>
<tr><td>Transaction records</td><td>7 years (legal requirement)</td></tr>
<tr><td>Usage logs</td><td>90 days</td></tr>
<tr><td>Marketing preferences</td><td>Until withdrawn</td></tr>
</tbody>
</table>

<hr />

<h2>9. Security Measures</h2>

<p>We implement appropriate technical and organisational measures to protect your personal information, including:</p>

<ul>
<li>Encryption of data in transit (TLS 1.2+) and at rest (AES-256)</li>
<li>Secure authentication and access controls</li>
<li>Regular security assessments</li>
<li>Employee training on data protection</li>
<li>Incident response procedures</li>
</ul>

<p>While we take all reasonable precautions, no method of transmission over the internet is 100% secure.</p>

<hr />

<h2>10. Your Rights Under POPIA</h2>

<p>You have the following rights regarding your personal information:</p>

<ul>
<li><strong>Right to access:</strong> Request a copy of your personal information</li>
<li><strong>Right to correction:</strong> Request correction of inaccurate information</li>
<li><strong>Right to deletion:</strong> Request deletion of your information (subject to legal retention requirements)</li>
<li><strong>Right to object:</strong> Object to processing for direct marketing</li>
<li><strong>Right to withdraw consent:</strong> Where processing is based on consent</li>
<li><strong>Right to lodge a complaint:</strong> With the Information Regulator</li>
</ul>

<p>To exercise these rights, contact our Information Officer at <a href="mailto:eben@suredox.co.za">eben@suredox.co.za</a>.</p>

<hr />

<h2>11. Cookies and Tracking</h2>

<p>We use essential cookies to:</p>

<ul>
<li>Maintain your session and authentication</li>
<li>Remember your preferences</li>
<li>Ensure security of our services</li>
</ul>

<p>We do not use third-party advertising or tracking cookies.</p>

<hr />

<h2>12. Children's Privacy</h2>

<p>Our services are not directed to children under 18 years of age. We do not knowingly collect personal information from children. If we become aware that we have collected personal information from a child, we will take steps to delete it.</p>

<hr />

<h2>13. Changes to This Policy</h2>

<p>We may update this Privacy Policy from time to time. We will notify you of material changes by email or through our website. The "Last Updated" date at the top of this policy indicates when it was last revised.</p>

<hr />

<h2>14. Contact Us</h2>

<p>For questions about this Privacy Policy or to exercise your rights:</p>

<p><strong>Information Officer:</strong> Eben Smit<br />
<strong>Email:</strong> <a href="mailto:eben@suredox.co.za">eben@suredox.co.za</a><br />
<strong>Address:</strong> 9 Clarence Avenue, Craighall Park, Johannesburg</p>

<p>You may also lodge a complaint with the Information Regulator:</p>

<p><strong>The Information Regulator (South Africa)</strong><br />
JD House, 27 Stiemens Street, Braamfontein, Johannesburg, 2001<br />
Email: <a href="mailto:inforeg@justice.gov.za">inforeg@justice.gov.za</a><br />
Website: <a href="https://www.justice.gov.za/inforeg" target="_blank" rel="noopener noreferrer">www.justice.gov.za/inforeg</a></p>
`;

export default privacyPolicyContent;
